import { Building2, KeyRound, User, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { usePortfolio } from '@/services/queries';
import { AccentCard, AccentStat } from '@/shared/ui/accent-card';
import { SectionHeading } from '@/shared/ui/section-heading';
import { Skeleton } from '@/shared/ui/skeleton';
import type { PortfolioSummary } from '@/types';
import { Sparkline } from './Sparkline';

/**
 * The estate's four headline totals, at the top of the dashboard.
 *
 * These used to live in the right-hand rail. They are the size of the
 * business rather than something to act on, but they are also the first thing
 * anybody glancing at the screen wants to know - and the rail was hidden below
 * xl, which took them off every laptop-width window.
 *
 * Same `AccentCard` as the licence pipeline underneath, so the two rows read
 * as one system rather than two designs stacked.
 */
interface Stat {
  key: string;
  label: string;
  value: number;
  icon: LucideIcon;
  trend: keyof Omit<PortfolioSummary['trends'], 'stepDays'>;
}

export function OverviewStats() {
  const query = usePortfolio(4);
  const summary = query.data;

  const stats: Stat[] = [
    {
      key: 'seatsTotal',
      label: 'Seats sold',
      value: summary?.seatsTotal ?? 0,
      icon: Users,
      trend: 'seatsTotal',
    },
    {
      key: 'licences',
      label: 'Licence Groups',
      value: summary?.licences ?? 0,
      icon: KeyRound,
      trend: 'licences',
    },
    {
      key: 'singleSeatLicences',
      label: 'Single seats',
      value: summary?.singleSeatLicences ?? 0,
      icon: User,
      trend: 'singleSeatLicences',
    },
    {
      key: 'organizations',
      label: 'Labs',
      value: summary?.organizations ?? 0,
      icon: Building2,
      trend: 'organizations',
    },
  ];

  return (
    <section>
      <SectionHeading size="lg">Overview</SectionHeading>

      <div className="grid gap-4.5 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <AccentCard key={stat.key} icon={<Icon size={16} strokeWidth={2} />} title={stat.label}>
              {query.isPending ? (
                <Skeleton className="h-7 w-14" />
              ) : (
                <AccentStat value={<span className="tabular-nums">{stat.value}</span>} />
              )}
              {/* Slate: how big the business is has no good or bad direction. */}
              {summary && (
                <Sparkline
                  className="mt-3"
                  points={summary.trends[stat.trend]}
                  stepDays={summary.trends.stepDays}
                  tone="neutral"
                  label={stat.label}
                />
              )}
            </AccentCard>
          );
        })}
      </div>
    </section>
  );
}
